<template>
  <v-modal
    id="the-case-day-item-pax-selection-modal"
    class-name="modal-fullscreen-sm-down"
    size="lg"
    @closed="closeModal"
  >
    <modal-header>
      <modal-title>Pax Selection</modal-title>
    </modal-header>

    <modal-body>
      <template v-if="selectedResource && selectedResource.length > 0">
        <div class="row">
          <div class="col-12 mb-8 d-flex align-items-center" v-for="p in selectedResource" :key="p.id">
            <div class="d-flex align-items-center">
              <v-checkbox
                :model-value="localSelectedPaxIds.includes(getTravellerId(p))"
                @update:model-value="(val) => togglePax(val, getTravellerId(p), p)"
                :id="`pax-selection-${getTravellerId(p)}`"
                :for-key="`pax-selection-${getTravellerId(p)}`"
                :label="' '"
                class="form-check-inline me-32"
                label-class="ms-0"
                :isDisabled="
                  isPaxUsedInAnotherResource(getTravellerId(p)) && !localSelectedPaxIds.includes(getTravellerId(p))
                "
              />

              <div class="ms-3">
                <div class="pax-name d-flex align-items-center">
                  {{ labelForPax(p).fullName }}
                  <div v-if="labelForPax(p).alreadySelected" class="text-danger small fw-bold ms-8">(already selected)</div>
                </div>

                <div v-if="labelForPax(p).email" class="pax-email">
                  {{ labelForPax(p).email }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <p class="fw-bold text-center">No paxes added to this resource.</p>
      </template>
    </modal-body>

    <modal-footer>
      <div class="d-flex gap-8">
        <v-button
          class="btn-sm btn-success align-self-end cursor-pointer"
          size="sm"
          variant="success"
          @click="$emit('closed')"
        >
          {{ $t("general.button.confirmSelection") }}
        </v-button>
      </div>
    </modal-footer>
  </v-modal>
</template>

<script setup lang="ts" name="TheCaseDayItemPaxSelection">
import { ref, PropType, watch } from "vue";
import VModal from "@/components/vendor/basic/modal/VModal.vue";
import ModalHeader from "@/components/vendor/basic/modal/ModalHeader.vue";
import ModalTitle from "@/components/vendor/basic/modal/ModalTitle.vue";
import ModalBody from "@/components/vendor/basic/modal/ModalBody.vue";
import ModalFooter from "@/components/vendor/basic/modal/ModalFooter.vue";
import { VCheckbox } from "@uniqoders/form";
import { useToast } from "vue-toastification";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import api from "@/api";

const emit = defineEmits<{
  (e: "closed"): void;
  (e: "refresh"): void;
}>();

const toast = useToast();

const props = defineProps({
  selectedResource: { type: Array as PropType<any[]>, required: true },
  agency: { type: String, required: true },
  caseId: { type: Number, required: true },
  caseDayId: { type: Number, required: true },
  itemId: { type: Number, required: true },
  caseDayItemResources: { type: Array as PropType<any[]>, required: true },
  supplierResourceId: { type: Number, required: true },
});

const localSelectedPaxIds = ref<number[]>([]);

const getTravellerId = (p: any): number | null => p?.traveller_id ?? p?.travellerId ?? p?.pax?.id ?? p?.id ?? null;

const findCurrentResourceObject = () => {
  if (!Array.isArray(props.caseDayItemResources)) return null;
  return (
    props.caseDayItemResources.find((r) => {
      const rSupplierId = r?.data?.supplier_resource_id ?? r?.supplier_resource_id ?? null;
      return Number(rSupplierId) === Number(props.supplierResourceId);
    }) ?? null
  );
};

const initializeLocalSelection = () => {
  localSelectedPaxIds.value = [];

  const currentResource = findCurrentResourceObject();
  if (!currentResource) {
    return;
  }

  const selectedList =
    currentResource?.data?.selectedPax ?? currentResource?.selectedPax ?? currentResource?.paxes ?? [];

  if (!Array.isArray(selectedList)) return;

  for (const s of selectedList) {
    const tid = s?.traveller_id ?? s?.travellerId ?? s?.pax?.id ?? s?.id ?? null;
    if (tid !== null && tid !== undefined) localSelectedPaxIds.value.push(Number(tid));
  }
};

const isPaxUsedInAnotherResource = (paxId: number | null): boolean => {
  if (!paxId) return false;
  if (!Array.isArray(props.caseDayItemResources)) return false;

  for (const resource of props.caseDayItemResources) {
    const resSupplierId = resource?.data?.supplier_resource_id ?? resource?.supplier_resource_id ?? null;
    // skip the currently opened/same supplier resource
    if (Number(resSupplierId) === Number(props.supplierResourceId)) continue;

    const otherSelected = resource?.data?.selectedPax ?? resource?.selectedPax ?? resource?.paxes ?? [];
    if (!Array.isArray(otherSelected)) continue;

    for (const px of otherSelected) {
      const otherTid = px?.traveller_id ?? px?.travellerId ?? px?.pax?.id ?? px?.id ?? null;
      if (otherTid !== null && Number(otherTid) === Number(paxId)) {
        return true;
      }
    }
  }
  return false;
};

// ---------- label builder ----------
const labelForPax = (p: any) => {
  console.log("labelForPax received:", JSON.parse(JSON.stringify(p)));
  
  // Attempt to find the "inner" traveller object if p is a wrapper
  const inner = p?.pax ?? p?.traveller ?? p;
  
  // Sometimes there's double nesting
  const data = inner?.pax ?? inner?.traveller ?? inner;
  
  // Try all possible name/surname fields
  const name = data?.name ?? inner?.name ?? p?.name ?? "";
  const surname = data?.surname ?? data?.surname1 ?? data?.surname2 ?? 
                  inner?.surname ?? inner?.surname1 ?? inner?.surname2 ?? 
                  p?.surname ?? p?.surname1 ?? p?.surname2 ?? "";
                  
  const alias = data?.alias ?? inner?.alias ?? p?.alias ?? "";
  
  let fullName = `${name} ${surname}`.trim();
  if (!fullName && alias) {
    fullName = alias;
  }
  if (!fullName) {
    fullName = "N/A";
  }
  
  const email = data?.email ?? inner?.email ?? p?.email ?? "";

  const travellerId = getTravellerId(p);
  const alreadySelected =
    travellerId && isPaxUsedInAnotherResource(travellerId) && !localSelectedPaxIds.value.includes(Number(travellerId));

  return {
    fullName,
    name,
    email,
    alreadySelected,
  };
};

const getCurrentCaseDayItemResourceId = (): number | null => {
  const r = findCurrentResourceObject();
  if (!r) return props.supplierResourceId ?? null;
  return r?.data?.id;
};

const togglePax = async (checked: boolean, paxId: number | null, p: any) => {
  if (!paxId) return;

  if (isPaxUsedInAnotherResource(paxId) && !localSelectedPaxIds.value.includes(Number(paxId))) return;

  const caseDayItemResourceId = getCurrentCaseDayItemResourceId();
  const traveller_id = getTravellerId(p) ?? paxId;

  if (!caseDayItemResourceId) {
    console.warn("Unable to resolve caseDayItemResourceId for API call — toggle aborted.");
    return;
  }

  if (checked) {
    if (!localSelectedPaxIds.value.includes(Number(paxId))) {
      try {
        await api.case.addCaseDayItemResourcePax(
          props.agency,
          props.caseId,
          props.caseDayId,
          props.itemId,
          caseDayItemResourceId,
          { traveller_id }
        );
        localSelectedPaxIds.value.push(Number(paxId));
      } catch (e: any) {
        console.error("addCaseDayItemResourcePax error", e);
        toast.error(e?.response?.data?.message ?? "Error adding pax");
      }
    }
  } else {
    const id =
      props.caseDayItemResources
        ?.find((i) => i.data?.supplier_resource_id === props.supplierResourceId)
        ?.data?.selectedPax?.find((sp: { traveller_id: number }) => sp.traveller_id === paxId)?.id ?? null;

    try {
      await api.case.removeCaseDayItemResourcePax(
        props.agency,
        props.caseId,
        props.caseDayId,
        props.itemId,
        caseDayItemResourceId,
        id
      );
      localSelectedPaxIds.value = localSelectedPaxIds.value.filter((id) => Number(id) !== Number(paxId));
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Error removing pax");
      console.error("removeCaseDayItemResourcePax error", e);
    }
  }

  emit("refresh");
};

const closeModal = () => {
  emit("closed");
};

watch(
  () => [props.selectedResource, props.caseDayItemResources, props.supplierResourceId],
  () => {
    initializeLocalSelection();
  },
  { immediate: true, deep: true }
);
</script>

<style scoped>
.pax-name {
  color: #000 !important;
  font-weight: 600;
}
.pax-email {
  color: #666 !important;
  font-size: 0.85rem;
}
</style>
